import { PermissionFlagsBits } from 'discord-api-types/v10';

// 한국어 권한 이름 매핑
export const PermissionNames: Record<string, string> = {
  CreateInstantInvite: '초대 코드 생성',
  KickMembers: '멤버 추방하기',
  BanMembers: '멤버 차단하기',
  Administrator: '관리자',
  ManageChannels: '채널 관리하기',
  ManageGuild: '서버 관리하기',
  AddReactions: '반응 추가하기',
  ViewAuditLog: '감사 로그 보기',
  PrioritySpeaker: '우선 발언권',
  Stream: '영상 전송',
  ViewChannel: '채널 보기',
  SendMessages: '메시지 보내기',
  SendTTSMessages: 'TTS 메시지 보내기',
  ManageMessages: '메시지 관리하기',
  EmbedLinks: '링크 첨부',
  AttachFiles: '파일 첨부',
  ReadMessageHistory: '메시지 기록 보기',
  MentionEveryone: '모두 멘션하기',
  UseExternalEmojis: '외부 이모티콘 사용하기',
  ViewGuildInsights: '서버 인사이트 보기',
  Connect: '연결',
  Speak: '말하기',
  MuteMembers: '멤버 음소거하기',
  DeafenMembers: '멤버 헤드셋 끄기',
  MoveMembers: '멤버 이동시키기',
  UseVAD: '음성 감지 사용',
  ChangeNickname: '별명 변경하기',
  ManageNicknames: '별명 관리하기',
  ManageRoles: '역할 관리하기',
  ManageWebhooks: '웹후크 관리하기',
  ManageEmojisAndStickers: '표현 관리하기',
  UseApplicationCommands: '애플리케이션 명령어 사용',
  RequestToSpeak: '발언권 요청',
  ManageEvents: '이벤트 관리',
  ManageThreads: '스레드 관리',
  CreatePublicThreads: '공개 스레드 만들기',
  CreatePrivateThreads: '비공개 스레드 만들기',
  UseExternalStickers: '외부 스티커 사용',
  SendMessagesInThreads: '스레드에서 메시지 보내기',
  UseEmbeddedActivities: '활동 사용',
  ModerateMembers: '멤버 중재하기',
  UseSoundboard: '사운드보드 사용',
  UseExternalSounds: '외부 사운드 사용',
  SendVoiceMessages: '음성 메시지 보내기',
  SendPolls: '투표 만들기',
  UseExternalApps: '외부 앱 사용',
  ViewCreatorMonetizationAnalytics: '크리에이터 수익 분석 보기',
};

// 권한 확인 함수
export function hasPermission(permissionsValue: string | number, permissionBit: bigint): boolean {
  const permissionsBigInt = typeof permissionsValue === 'string' 
    ? BigInt(permissionsValue) 
    : BigInt(permissionsValue);
  
  return (permissionsBigInt & permissionBit) === permissionBit;
}

// 권한 이름 목록 가져오기
export function getPermissionNames(permissionsValue: string | number): string[] {
  const permNames: string[] = [];
  
  for (const [name, bit] of Object.entries(PermissionFlagsBits)) {
    if (hasPermission(permissionsValue, bit)) {
      permNames.push(PermissionNames[name] || name);
    }
  }
  
  return permNames;
}

// 중요 권한 확인 (서버 관리에 필요한 주요 권한)
export function hasImportantPermissions(permissionsValue: string | number): boolean {
  const importantPermissions = [
    PermissionFlagsBits.Administrator,
    PermissionFlagsBits.ManageGuild,
    PermissionFlagsBits.ManageRoles,
    PermissionFlagsBits.ManageChannels
  ];
  
  return importantPermissions.some(perm => hasPermission(permissionsValue, perm));
}

// 권한 설명을 가져오는 함수 추가
export function getPermissionDescription(permissionsValue: string | number): {
  status: 'admin' | 'manager' | 'limited' | 'none';
  description: string;
} {
  // 관리자 권한
  if (hasPermission(permissionsValue, PermissionFlagsBits.Administrator)) {
    return {
      status: 'admin',
      description: '관리자 권한 (모든 기능 사용 가능)'
    };
  }
  
  // 서버 관리 권한
  if (hasPermission(permissionsValue, PermissionFlagsBits.ManageGuild)) {
    return {
      status: 'manager',
      description: '서버 관리 권한 (대부분의 기능 사용 가능)'
    };
  }
  
  // 채널 또는 역할 관리 권한
  if (
    hasPermission(permissionsValue, PermissionFlagsBits.ManageChannels) || 
    hasPermission(permissionsValue, PermissionFlagsBits.ManageRoles)
  ) {
    return {
      status: 'limited',
      description: '제한된 관리 권한 (일부 기능만 사용 가능)'
    };
  }
  
  // 관리 권한 없음
  return {
    status: 'none',
    description: '관리 권한 없음 (기능 사용에 제한이 있을 수 있음)'
  };
}

// 필요한 권한 목록
export const requiredPermissions = {
  basic: [
    PermissionFlagsBits.ViewChannel,
    PermissionFlagsBits.SendMessages,
    PermissionFlagsBits.ReadMessageHistory,
    PermissionFlagsBits.Connect,
    PermissionFlagsBits.Speak
  ],
  standard: [
    PermissionFlagsBits.ManageMessages,
    PermissionFlagsBits.EmbedLinks,
    PermissionFlagsBits.AttachFiles,
    PermissionFlagsBits.UseExternalEmojis,
    PermissionFlagsBits.AddReactions
  ],
  advanced: [
    PermissionFlagsBits.ManageChannels,
    PermissionFlagsBits.ManageRoles,
    PermissionFlagsBits.MoveMembers
  ],
  admin: [
    PermissionFlagsBits.Administrator
  ]
};

// 권한 부족 여부 확인
export function checkMissingPermissions(permissionsValue: string | number, permissionsToCheck: bigint[]): string[] {
  const missingPermissions: string[] = [];
  
  for (const permission of permissionsToCheck) {
    if (!hasPermission(permissionsValue, permission)) {
      // 권한 이름 찾기
      const permName = Object.entries(PermissionFlagsBits).find(([, bit]) => bit === permission)?.[0];
      if (permName && PermissionNames[permName]) {
        missingPermissions.push(PermissionNames[permName]);
      }
    }
  }
  
  return missingPermissions;
}

// 카미봇 필수 권한 정의
export const kamibotRequiredPermissions = [
  {
    bit: PermissionFlagsBits.SendMessages,
    name: '메시지 보내기',
    description: '채널에 메시지를 보내기 위해 필요합니다.'
  },
  {
    bit: PermissionFlagsBits.EmbedLinks,
    name: '링크 첨부',
    description: '임베드 메시지를 보내기 위해 필요합니다.'
  },
  {
    bit: PermissionFlagsBits.AttachFiles,
    name: '파일 첨부',
    description: '이미지와 파일을 전송하기 위해 필요합니다.'
  },
  {
    bit: PermissionFlagsBits.ReadMessageHistory,
    name: '메시지 기록 보기',
    description: '과거 메시지를 읽고 처리하기 위해 필요합니다.'
  },
  {
    bit: PermissionFlagsBits.UseExternalEmojis,
    name: '외부 이모티콘 사용하기',
    description: '카미봇의 커스텀 이모티콘을 사용하기 위해 필요합니다.'
  }
];

// 카미봇 선택적 권한 정의 (특정 기능을 위한 권한)
export const kamibotOptionalPermissions = [
  {
    bit: PermissionFlagsBits.Connect,
    name: '연결',
    description: 'TTS 기능을 위해 음성 채널에 연결해야 합니다.',
    features: ['TTS 기능']
  },
  {
    bit: PermissionFlagsBits.Speak,
    name: '말하기',
    description: 'TTS 메시지를 읽기 위해 필요합니다.',
    features: ['TTS 기능']
  },
  {
    bit: PermissionFlagsBits.ManageMessages,
    name: '메시지 관리하기',
    description: '메시지 삭제 및 관리 기능에 필요합니다.',
    features: ['메시지 자동 삭제', '메시지 매니저']
  },
  {
    bit: PermissionFlagsBits.ManageWebhooks,
    name: '웹후크 관리하기',
    description: '알림 설정 및 통합 기능에 필요합니다.',
    features: ['알림 설정', '외부 서비스 연동']
  },
  {
    bit: PermissionFlagsBits.ManageRoles,
    name: '역할 관리하기',
    description: '자동 역할 부여 기능에 필요합니다.',
    features: ['자동 역할', '티켓 시스템']
  },
  {
    bit: PermissionFlagsBits.ManageChannels,
    name: '채널 관리하기',
    description: '임시 채널 생성 및 관리에 필요합니다.',
    features: ['임시 음성 채널', '채널 매니저']
  }
];

// 사용자 필수 권한 (서버/채널 설정 변경을 위한 권한)
export const userRequiredPermissions = [
  {
    bit: PermissionFlagsBits.Administrator,
    name: '관리자',
    description: '모든 서버 설정을 변경할 수 있는 권한입니다.'
  },
  {
    bit: PermissionFlagsBits.ManageGuild,
    name: '서버 관리하기',
    description: '서버 설정을 변경할 수 있는 권한입니다.'
  }
];

// 사용자가 서버 설정을 변경할 수 있는지 확인하는 함수
export function canUserManageSettings(userPermissionsValue: string | number): boolean {
  return userRequiredPermissions.some(perm => 
    hasPermission(userPermissionsValue, perm.bit)
  );
}

// 카미봇이 필수 기능을 수행할 수 있는지 확인하는 함수
export function kamibotHasRequiredPermissions(botPermissionsValue: string | number): boolean {
  return kamibotRequiredPermissions.every(perm => 
    hasPermission(botPermissionsValue, perm.bit)
  );
}

// 카미봇에게 없는 필수 권한 목록 가져오기
export function kamibotMissingRequiredPermissions(botPermissionsValue: string | number): typeof kamibotRequiredPermissions {
  return kamibotRequiredPermissions.filter(perm => 
    !hasPermission(botPermissionsValue, perm.bit)
  );
}

// 카미봇에게 없는 선택적 권한 목록 가져오기
export function kamibotMissingOptionalPermissions(botPermissionsValue: string | number): typeof kamibotOptionalPermissions {
  return kamibotOptionalPermissions.filter(perm => 
    !hasPermission(botPermissionsValue, perm.bit)
  );
}

// 명시적으로 타입 정의
export interface SettingsPermissionStatus {
  canChange: boolean;
  userHasPermission: boolean;
  botHasRequiredPermissions: boolean;
  userPermissionStatus: string;
  botPermissionStatus: string;
  missingBotPermissions: typeof kamibotRequiredPermissions;
}

// 사용자가 설정을 변경할 수 있는지와 카미봇이 필요한 권한이 있는지 함께 확인
export function canChangeSettings(
  userPermissionsValue: string | number,
  botPermissionsValue: string | number
): SettingsPermissionStatus {
  const userHasPermission = canUserManageSettings(userPermissionsValue);
  const botHasPermissions = kamibotHasRequiredPermissions(botPermissionsValue);
  const missingBotPermissions = kamibotMissingRequiredPermissions(botPermissionsValue);
  
  let userPermissionStatus = '';
  if (hasPermission(userPermissionsValue, PermissionFlagsBits.Administrator)) {
    userPermissionStatus = '관리자 권한';
  } else if (hasPermission(userPermissionsValue, PermissionFlagsBits.ManageGuild)) {
    userPermissionStatus = '서버 관리 권한';
  } else {
    userPermissionStatus = '권한 부족';
  }
  
  let botPermissionStatus = '';
  if (hasPermission(botPermissionsValue, PermissionFlagsBits.Administrator)) {
    botPermissionStatus = '모든 권한 있음';
  } else if (botHasPermissions) {
    botPermissionStatus = '필수 권한 있음';
  } else {
    botPermissionStatus = '권한 부족';
  }
  
  return {
    canChange: userHasPermission && botHasPermissions,
    userHasPermission,
    botHasRequiredPermissions: botHasPermissions,
    userPermissionStatus,
    botPermissionStatus,
    missingBotPermissions
  };
}

// 사용자가 채널을 볼 수 있는지 확인하는 함수
export function canViewChannel(permissionsValue: string | number): boolean {
  return hasPermission(permissionsValue, PermissionFlagsBits.ViewChannel);
}

// 사용자가 채널에서 메시지를 읽을 수 있는지 확인하는 함수 (채널 보기 + 메시지 기록 보기)
export function canReadMessages(permissionsValue: string | number): boolean {
  return hasPermission(permissionsValue, PermissionFlagsBits.ViewChannel) &&
         hasPermission(permissionsValue, PermissionFlagsBits.ReadMessageHistory);
}

// 사용자가 채널에서 메시지를 보낼 수 있는지 확인하는 함수 (채널 보기 + 메시지 보내기)
export function canSendMessages(permissionsValue: string | number): boolean {
  return hasPermission(permissionsValue, PermissionFlagsBits.ViewChannel) &&
         hasPermission(permissionsValue, PermissionFlagsBits.SendMessages);
}

// 사용자가 음성 채널에 연결할 수 있는지 확인하는 함수 (채널 보기 + 연결)
export function canConnectToVoice(permissionsValue: string | number): boolean {
  return hasPermission(permissionsValue, PermissionFlagsBits.ViewChannel) &&
         hasPermission(permissionsValue, PermissionFlagsBits.Connect);
}

// 채널 접근 레벨을 확인하는 함수
export function getChannelAccessLevel(permissionsValue: string | number): {
  level: 'none' | 'view' | 'read' | 'write' | 'manage';
  description: string;
  permissions: string[];
} {
  const permissions: string[] = [];
  
  // 채널을 볼 수 없음
  if (!hasPermission(permissionsValue, PermissionFlagsBits.ViewChannel)) {
    return {
      level: 'none',
      description: '채널에 접근할 수 없습니다',
      permissions: []
    };
  }
  
  permissions.push('채널 보기');
  
  // 메시지 기록을 읽을 수 있음
  const canRead = hasPermission(permissionsValue, PermissionFlagsBits.ReadMessageHistory);
  if (canRead) {
    permissions.push('메시지 기록 보기');
  }
  
  // 메시지를 보낼 수 있음
  const canWrite = hasPermission(permissionsValue, PermissionFlagsBits.SendMessages);
  if (canWrite) {
    permissions.push('메시지 보내기');
  }
  
  // 메시지를 관리할 수 있음
  const canManage = hasPermission(permissionsValue, PermissionFlagsBits.ManageMessages);
  if (canManage) {
    permissions.push('메시지 관리하기');
  }
  
  // 접근 레벨 결정
  if (canManage) {
    return {
      level: 'manage',
      description: '채널을 완전히 관리할 수 있습니다',
      permissions
    };
  } else if (canWrite) {
    return {
      level: 'write',
      description: '채널에서 메시지를 읽고 쓸 수 있습니다',
      permissions
    };
  } else if (canRead) {
    return {
      level: 'read',
      description: '채널에서 메시지를 읽을 수 있습니다',
      permissions
    };
  } else {
    return {
      level: 'view',
      description: '채널을 볼 수 있지만 메시지는 읽을 수 없습니다',
      permissions
    };
  }
}